import { useQueryClient } from "@tanstack/react-query";
import { Button, Section, StateView, useAuth, useToast } from "@ozpanel/ui";
import { NotificationLines } from "./NotificationLines";
import { type FeedRow } from "./notificationGroups";
import { useClearNotifications } from "./ClearNotifications";
import { useNotificationLists } from "./useNotificationLists";

// NotificationFeed — the caller's own recent notices, as a full page section rather than a
// transient dropdown. /me/notifications acts on the CALLER, so the server decides the scope and
// this one component serves every console. It existed inline in the account console and NOWHERE in
// the mailbox console, so a mailbox user's sending-limit / forward / bounce notices reached them
// only through the NotificationBell — which clears when dismissed and cannot be scrolled back to.
//
// The row shape is notifications.go's own: the headline is `summary` (there is no `title` — reading
// one showed a blank), and `consequence` is the sentence that says what the notice MEANS, which is
// the difference between a feed and a list of nouns.

export type NotificationItem = FeedRow;

export function NotificationFeed({
  title = "Recent",
  limit = 25,
  className,
  audience = "customer",
}: {
  title?: string;
  limit?: number;
  className?: string;
  /** The feed page lives in the customer consoles; server names there are a technical detail. */
  audience?: "operator" | "customer";
}) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const clear = useClearNotifications();

  // One line per fault, folded across nodes; every open fault first (asked for by state, so an old
  // one is not lost behind the window), then what ended or happened recently.
  const { recent: feed, items, openGroups, earlier, openKnown } = useNotificationLists(limit);
  const hasAny = items.length > 0 || openGroups.length > 0;

  return (
    <Section
      title={title}
      className={className}
      hint={openKnown ? `${openGroups.length} open` : feed.data?.total != null ? `${feed.data.total}` : undefined}
      bodyClassName="p-0"
    >
      <div className="p-4">
        <StateView loading={feed.isLoading} error={feed.error} errorContext="your notifications">
          {!hasAny ? (
            <div className="text-[13px] text-muted">Nothing yet. Notices about your account and mail will appear here.</div>
          ) : (
            <div className="-mx-3">
              {openKnown && (
                <>
                  <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Open now · {openGroups.length}</div>
                  <NotificationLines audience={audience} groups={openGroups} empty={<div className="px-3 py-2 text-[12.5px] text-muted">Nothing is open.</div>} />
                  <div className="px-3 pb-1 pt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Earlier — ended, or happened</div>
                </>
              )}
              {/* A server that cannot count what is open has no "Open now" split: everything, as it came. */}
              <NotificationLines audience={audience} groups={openKnown ? earlier : [...openGroups, ...earlier]} />
            </div>
          )}
        </StateView>
      </div>
      {clear.dialog}
      {/* Only when there is something to act on: three disabled buttons under "Nothing yet" read as
          controls that are broken. */}
      {hasAny && (
      <div className="flex flex-wrap gap-2 border-t border-line px-4 py-2.5">
        <Button
          size="sm"
          variant="ghost"
          disabled={!hasAny}
          onClick={() =>
            void client
              .apiFetch("/me/notifications/read", { method: "POST", body: JSON.stringify({ all: true }) })
              .then(() => {
                notify({ kind: "success", title: "Marked as read" });
                void qc.invalidateQueries({ queryKey: ["me-notifications"] });
              })
              .catch((e: unknown) => notify({ kind: "error", title: "Could not mark them read", body: e instanceof Error ? e.message : String(e) }))
          }
        >
          Mark everything read
        </Button>
        <Button size="sm" variant="ghost" disabled={!hasAny} onClick={() => clear.request("read")}>
          Clear read
        </Button>
        <Button size="sm" variant="danger" disabled={!hasAny} onClick={() => clear.request("all")}>
          Clear all
        </Button>
      </div>
      )}
    </Section>
  );
}
